import { useRef, useState } from 'react';
import { DataGrid } from '../../components/grid/DataGrid';
import { usePuo } from '../sessione/ContestoSessione';
import { Corrispondenze } from './Corrispondenze';
import { Abbinamenti } from './Abbinamenti';
import { ScontiConcordati } from './ScontiConcordati';
import { listini } from '../../api/listini';
import type { EsitoImportazioneListino } from '../../api/listini';

const INTESTAZIONI: Record<string, string> = {
  Avviata: 'Quando',
  NomeFile: 'File',
  RigheLette: 'Righe',
  RigheAccettate: 'Buone',
  RigheScartate: 'Scartate',
  OfferteCreate: 'Nuove',
  OfferteAggiornate: 'Cambiate',
  OfferteChiuse: 'Chiuse',
  DurataSecondi: 'Secondi',
  AvviataDa: 'Chi',
};

const COLONNE = [
  'Avviata',
  'Esito',
  'NomeFile',
  'Distributore',
  'RigheLette',
  'RigheAccettate',
  'RigheScartate',
  'OfferteCreate',
  'OfferteAggiornate',
  'OfferteChiuse',
  'DurataSecondi',
  'AvviataDa',
];

const ESTENSIONI = '.xlsx,.xlsm,.csv,.txt';

/*  I listini: com'è andata, e le due porte per farne entrare uno adesso.

    La griglia è il pezzo importante, ed è il «log tabellare» chiesto in riunione. La
    ragione per cui esiste sta tutta in una frase: un'importazione notturna nessuno la
    guarda mentre succede, e se non lascia una riga leggibile la mattina dopo, il primo
    modo di accorgersi che un listino non è entrato è un ordine fatto al prezzo vecchio.

    Le tre parole nella colonna Esito vogliono dire tre cose diverse, e vale la pena
    saperle distinguere prima di aprire la riga:

        Completata                 il file è dentro, ed è finito in «processato»
        Fornitore da riconoscere   manca una riga di corrispondenza; il file è ancora
                                   nella cartella e aspetta
        Fallita                    il file non si è potuto leggere ed è finito in «errore»

    Il secondo caso non è un errore ed è il motivo per cui il pannello delle
    corrispondenze sta su questa schermata e non fra le impostazioni: si legge «COMINOX
    non è fra le corrispondenze», si scrive la riga lì accanto, si rilancia la passata, e
    il file entra. Tre gesti nello stesso posto.
*/
export function ElencoListini() {
  const puo = usePuo();
  const puoImportare = puo('ImportaListini');

  const [aperta, setAperta] = useState<Record<string, unknown> | null>(null);
  const [corrispondenzeAperte, setCorrispondenzeAperte] = useState(false);
  const [abbinamentiAperti, setAbbinamentiAperti] = useState(false);
  const [scontiAperti, setScontiAperti] = useState(false);
  const [versione, setVersione] = useState(0);
  const [inCorso, setInCorso] = useState<'passata' | 'caricamento' | null>(null);
  const [esiti, setEsiti] = useState<EsitoImportazioneListino[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const scelta = useRef<HTMLInputElement>(null);

  const concludi = (nuovi: EsitoImportazioneListino[]) => {
    setEsiti(nuovi);
    setErrore(null);
    setVersione((v) => v + 1);
  };

  const fallisci = (e: unknown) => {
    setEsiti(null);
    setErrore(e instanceof Error ? e.message : String(e));
  };

  const lanciaPassata = () => {
    setInCorso('passata');
    listini
      .passata()
      .then(concludi)
      .catch(fallisci)
      .finally(() => setInCorso(null));
  };

  const carica = (file: File) => {
    setInCorso('caricamento');
    listini
      .importa(file)
      .then((esito) => concludi([esito]))
      .catch(fallisci)
      .finally(() => setInCorso(null));
  };

  return (
    <section className="pagina con-scheda">
      <div className="colonna-principale">
        <header className="testata-pagina">
          <div>
            <h1>Listini</h1>
            <p>
              Ogni file passato dalla cartella sorvegliata, con quante righe sono entrate
              e quante offerte ne sono uscite. L'agente guarda la cartella di notte: qui
              si vede com'è andata, e si può farlo guardare adesso.
            </p>
            <p className="nota">
              <strong>Fornitore da riconoscere</strong> non è un errore: il file è ancora
              nella cartella e aspetta che qualcuno dica di chi è. Dichiaralo fra le{' '}
              <button
                type="button"
                className="collegamento"
                onClick={() => {
                  setCorrispondenzeAperte(true);
                  setScontiAperti(false);
                  setAbbinamentiAperti(false);
                }}
              >
                corrispondenze
              </button>{' '}
              e alla passata dopo entra da solo.
            </p>
            {/*  Il secondo pannello di questa schermata, e sta qui per la stessa ragione
                 del primo: il momento in cui serve è subito dopo aver importato il
                 catalogo di un grossista. Un articolo che lui chiama a modo suo resta un
                 articolo a sé, e il confronto prezzi mostra un fornitore solo dove ce ne
                 sono due.  */}
            <p className="nota">
              Un grossista chiama i codici a modo suo: «01376#031» dove il produttore
              scrive «01376 031». Quelli che sarebbero lo stesso articolo si guardano fra
              gli{' '}
              <button
                type="button"
                className="collegamento"
                onClick={() => {
                  setAbbinamentiAperti(true);
                  setCorrispondenzeAperte(false);
                  setScontiAperti(false);
                  setAperta(null);
                }}
              >
                abbinamenti
              </button>
              , e finché nessuno li conferma il confronto non li mette insieme.
            </p>
            {/*  Il terzo pannello. Sta qui perché è qui che agisce: lo sconto concordato
                 lo consulta l'importazione, e chi lo scrive è la stessa persona che
                 guarda entrare i listini.  */}
            <p className="nota">
              Lo sconto che ci fa un fornitore su una marca oggi è scritto a mano dentro
              ogni file. Gli{' '}
              <button
                type="button"
                className="collegamento"
                onClick={() => {
                  setScontiAperti(true);
                  setAbbinamentiAperti(false);
                  setCorrispondenzeAperte(false);
                  setAperta(null);
                }}
              >
                sconti concordati
              </button>{' '}
              lo tolgono dai file: da lì in poi decide l'accordo, e l'importazione dice
              dove ha scavalcato il file e di quanto.
            </p>
          </div>

          {puoImportare && (
            <div className="comandi-testata">
              <input
                ref={scelta}
                type="file"
                accept={ESTENSIONI}
                hidden
                onChange={(evento) => {
                  const file = evento.target.files?.[0];
                  // Il campo si azzera perché lo stesso file, ricaricato due volte di
                  // fila, non fa scattare `change` se il valore non cambia.
                  evento.target.value = '';
                  if (file) carica(file);
                }}
              />
              <button
                type="button"
                onClick={() => scelta.current?.click()}
                disabled={inCorso !== null}
              >
                {inCorso === 'caricamento' ? 'Caricamento…' : 'Carica un file'}
              </button>
              <button
                type="button"
                className="primario"
                onClick={lanciaPassata}
                disabled={inCorso !== null}
              >
                {inCorso === 'passata' ? 'In corso…' : 'Guarda la cartella'}
              </button>
            </div>
          )}
        </header>

        {errore !== null && <p className="errore">{errore}</p>}

        {esiti !== null && (
          <div className="esito-passata">
            {esiti.length === 0 ? (
              <p>Nella cartella non c'era niente di nuovo.</p>
            ) : (
              <ul>
                {esiti.map((esito) => (
                  <li key={esito.id} className={esito.riuscita ? 'buono' : 'da-guardare'}>
                    <strong>{esito.nomeFile}</strong>{' '}
                    {esito.riuscita ? (
                      <>
                        {esito.righeAccettate} righe su {esito.righeLette}; offerte{' '}
                        +{esito.offerteCreate} ~{esito.offerteAggiornate} −
                        {esito.offerteChiuse}
                      </>
                    ) : (
                      (esito.messaggio ?? esito.esito)
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DataGrid
          nomeGriglia="listini"
          versione={versione}
          colonneVisibili={COLONNE}
          intestazioni={INTESTAZIONI}
          idSelezionato={typeof aperta?.['Id'] === 'string' ? (aperta['Id'] as string) : null}
          onRigaSelezionata={(riga) => {
            setCorrispondenzeAperte(false);
            setAbbinamentiAperti(false);
            setScontiAperti(false);
            setAperta(riga);
          }}
        />
      </div>

      {corrispondenzeAperte && (
        <Corrispondenze
          modificabili={puoImportare}
          onChiudi={() => setCorrispondenzeAperte(false)}
        />
      )}

      {abbinamentiAperti && (
        <Abbinamenti
          decidibili={puoImportare}
          onChiuso={() => setAbbinamentiAperti(false)}
        />
      )}

      {scontiAperti && (
        <ScontiConcordati
          modificabili={puoImportare}
          onChiuso={() => setScontiAperti(false)}
        />
      )}

      {!corrispondenzeAperte && !abbinamentiAperti && !scontiAperti && aperta !== null && (
        <aside className="scheda">
          <header>
            <div>
              <h2>{String(aperta['NomeFile'] ?? '')}</h2>
              <p className="dettaglio">
                {String(aperta['Esito'] ?? '')}
                {aperta['Distributore'] ? ` · ${String(aperta['Distributore'])}` : ''}
              </p>
            </div>
            <button
              type="button"
              className="chiudi"
              onClick={() => setAperta(null)}
              aria-label="Chiudi"
            >
              ×
            </button>
          </header>

          {/*  Messaggio e scarti stanno già nella riga della griglia: non si richiede
               niente al server per aprirli. In griglia però sono illeggibili — sono due
               colonne di testo lungo — e qui hanno lo spazio per essere letti, che è
               l'unica ragione per cui questo pannello esiste.  */}
          {typeof aperta['Messaggio'] === 'string' && aperta['Messaggio'].length > 0 && (
            <section className="blocco">
              <h3>Cosa è successo</h3>
              <p>{aperta['Messaggio']}</p>
            </section>
          )}

          {typeof aperta['Scarti'] === 'string' && aperta['Scarti'].length > 0 && (
            <section className="blocco">
              <h3>Righe scartate</h3>
              <p className="scarti">{aperta['Scarti']}</p>
              <p className="nota">
                I numeri di riga sono quelli del file: aprendolo e andando a quella riga
                si vede la cella che non si è potuta leggere.
              </p>
            </section>
          )}

          {aperta['Messaggio'] === null && aperta['Scarti'] === null && (
            <p className="nota">
              Nessuno scarto e nessun problema: il file è entrato tutto.
            </p>
          )}
        </aside>
      )}
    </section>
  );
}
